import type { AgentDto } from '@af/shared';
import { fuzzyRank } from '@/lib/fuzzy';
import { safeStorage } from '@/lib/utils';

export type LauncherGroup =
  | 'recent'
  | 'setup'
  | 'send'
  | 'navigate'
  | 'agents'
  | 'conversations'
  | 'runs'
  | 'tasks'
  | 'approvals'
  | 'settings'
  | 'actions'
  | 'dev';

/** Group display order. */
export const GROUP_ORDER: LauncherGroup[] = [
  'send',
  'recent',
  'setup',
  'navigate',
  'agents',
  'actions',
  'approvals',
  'conversations',
  'runs',
  'tasks',
  'settings',
  'dev',
];

export type LauncherIcon =
  | 'home'
  | 'company'
  | 'agents'
  | 'chat'
  | 'tasks'
  | 'runs'
  | 'approvals'
  | 'usage'
  | 'notifications'
  | 'settings'
  | 'office'
  | 'plus'
  | 'stop'
  | 'play'
  | 'theme'
  | 'org'
  | 'keyboard'
  | 'sparkle'
  | 'help'
  | 'agent'
  | 'pause'
  | 'lang'
  | 'check';

export interface LauncherItem {
  /** Stable id (used for recents), e.g. `nav:/agents`, `agent:agt_x`. */
  id: string;
  group: LauncherGroup;
  title: string;
  subtitle?: string;
  keywords?: string;
  icon: LauncherIcon;
  /** Seed for an agent orb instead of an icon. */
  orb?: string;
  /** Keyboard hint shown on the row (e.g. `g a`). */
  hint?: string;
  /** Shown in `>` command mode. */
  command?: boolean;
  run: () => void;
  secondary?: { label: string; run: () => void };
  /** Tab drills into these sub-actions. */
  drill?: () => LauncherItem[];
  /** Agent handle for `@` completion. */
  agentSlug?: string;
}

export type ParsedQuery =
  | { mode: 'search'; q: string }
  | { mode: 'command'; q: string }
  | { mode: 'mention'; handle: string; message: string; hasSpace: boolean };

export function parseQuery(input: string): ParsedQuery {
  if (input.startsWith('>')) return { mode: 'command', q: input.slice(1).trim() };
  const m = /^@(\S*)(\s+([\s\S]*))?$/.exec(input);
  if (m) return { mode: 'mention', handle: m[1] ?? '', message: (m[3] ?? '').trim(), hasSpace: !!m[2] };
  return { mode: 'search', q: input.trim() };
}

/** Resolve `@handle` to an agent: exact slug/name first, then the best fuzzy match. */
export function resolveAgent(handle: string, agents: AgentDto[]): AgentDto | undefined {
  const h = handle.toLowerCase();
  if (!h) return undefined;
  const live = agents.filter((a) => a.status !== 'archived');
  return (
    live.find((a) => a.slug.toLowerCase() === h || a.name.toLowerCase() === h) ??
    fuzzyRank(live, h, (a) => [a.slug, a.name, a.roleTitle])[0]
  );
}

/** Search a flat index; results grouped in display order, capped per group. */
export function searchItems(items: LauncherItem[], q: string, perGroup = 6): LauncherItem[] {
  const ranked = fuzzyRank(items, q, (i) => [i.title, i.keywords, i.subtitle]);
  const counts = new Map<LauncherGroup, number>();
  const kept = ranked.filter((i) => {
    const n = counts.get(i.group) ?? 0;
    counts.set(i.group, n + 1);
    return n < perGroup;
  });
  // Keep groups contiguous: order groups by their best hit, items inside by rank.
  const firstHit = new Map<LauncherGroup, number>();
  kept.forEach((i, idx) => {
    if (!firstHit.has(i.group)) firstHit.set(i.group, idx);
  });
  return [...kept].sort((a, b) => firstHit.get(a.group)! - firstHit.get(b.group)!);
}

/** Order items by GROUP_ORDER (stable inside a group). */
export function byGroupOrder(items: LauncherItem[]): LauncherItem[] {
  return [...items].sort((a, b) => GROUP_ORDER.indexOf(a.group) - GROUP_ORDER.indexOf(b.group));
}

// ---------- recents ----------
const RECENT_KEY = 'af:launcher-recent';
const RECENT_MAX = 6;

export function loadRecent(): string[] {
  const v = safeStorage.get<string[]>(RECENT_KEY);
  return Array.isArray(v) ? v.filter((x) => typeof x === 'string').slice(0, RECENT_MAX) : [];
}

export function pushRecent(id: string) {
  safeStorage.set(RECENT_KEY, [id, ...loadRecent().filter((x) => x !== id)].slice(0, RECENT_MAX));
}
